/**
 * Binds the persistence checkpoint to the settings profile client: the store
 * builds the profile, the existing client saves and re-fetches it, and the
 * existing DTO reader turns both ends into observations. Serialization stays
 * where it already lives.
 */

import {
  SettingsType,
  buildPcfSettingsDtoFromStore,
  getPipeexSettings,
  type PcfSettingsDto,
} from '../../../shared/api/pipeex/pcfSettingsClient';
import { observeProfileConnectorSetup } from './profileReadBackObservation';
import {
  runProfilePersistenceCheckpoint,
  type ProfilePersistenceCheckpoint,
} from './profilePersistenceCheckpoint';
import type { SetupDocumentIdentity } from '../../../shared/lib/setupRevision';

/** The authoritative key a saved profile is fetched back by. */
export interface PcfProfileCheckpointTarget {
  name: string;
  settingsType: SettingsType;
}

export interface PcfProfileCheckpointArgs {
  /** The revision this checkpoint speaks for. */
  revisionId: number;
  currentRevisionId: () => number;
  /** `null` when no Revit document identifies the setup. */
  identity: SetupDocumentIdentity | null;
  /** `null` when the save names no profile that could be fetched back. */
  target: PcfProfileCheckpointTarget | null;
  save: () => Promise<unknown>;
  isSuperseded?: () => boolean;
}

export type PcfProfileCheckpoint = ProfilePersistenceCheckpoint<PcfSettingsDto>;

const neverSuperseded = () => false;

export const runPcfProfileCheckpoint = (
  args: PcfProfileCheckpointArgs
): Promise<PcfProfileCheckpoint> =>
  runProfilePersistenceCheckpoint<PcfSettingsDto>({
    revisionId: args.revisionId,
    // Frozen before the save runs, so a later store edit cannot pass as verified.
    intendedProfile: buildPcfSettingsDtoFromStore(args.target?.settingsType ?? SettingsType.User),
    observe: (profile) => observeProfileConnectorSetup(profile, args.identity),
    save: async () => {
      await args.save();
    },
    readBack: () =>
      args.target
        ? getPipeexSettings(args.target.name, args.target.settingsType)
        : Promise.resolve(null),
    currentRevisionId: args.currentRevisionId,
    isSuperseded: args.isSuperseded ?? neverSuperseded,
  });
